import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AppData } from '../helpers/app-data';

export interface CatalogoVariableReporteDTO {
  NombreVariable: string;
  TipoDato: string;
  Origen: string;
  EsCampoNativo?: boolean;
}

export interface VistaBaseDatosResponseDTO {
  NombreVista: string;
}

export interface ReporteriaColumnaFrontendMeta {
  NombreVariable: string;
  Etiqueta: string;
  Orden: number;
  VisibleEnTabla: boolean;
  TipoControl: 'input' | 'areatext';
  EsCampoNativo: boolean;
  ColumnaReferenciaVista?: string;
}

export interface HistoricoSpConfiguracionDTO {
  NombreSp: string;
  VariableEntrada: string;
  VariableReporte: string;
}

export interface ReporteriaProcesoFrontendMeta {
  TipoOrigen: 'Variables' | 'Vista';
  NombreVista?: string;
  HabilitarBusquedaFechas: boolean;
  ColumnaFechaVista?: string;
  HabilitarHistorico: boolean;
  ModoHistorico?: 'default' | 'sp';
  ConfiguracionHistoricoSp?: HistoricoSpConfiguracionDTO[];
  Columnas: ReporteriaColumnaFrontendMeta[];
}

export interface ConfiguracionReporteColumnaResponseDTO {
  Id: number;
  IdProceso: number;
  NombreVariable: string;
  Etiqueta: string;
  Orden: number;
  TipoOrigen: 'Variables' | 'Vista';
  NombreVista?: string;
  VisibleEnTabla: boolean;
  TipoControl: 'input' | 'areatext';
  EsCampoNativo: boolean;
  ColumnaReferenciaVista?: string;
  HabilitarBusquedaFechas: boolean;
  ColumnaFechaVista?: string;
  HabilitarHistorico: boolean;
  ModoHistorico?: 'default' | 'sp';
  ConfiguracionHistoricoSp?: HistoricoSpConfiguracionDTO[];
}

export interface ConfiguracionReporteColumnaRequestDTO {
  NombreVariable: string;
  Etiqueta: string;
  Orden: number;
  VisibleEnTabla?: boolean;
  TipoControl?: 'input' | 'areatext';
  EsCampoNativo?: boolean;
  ColumnaReferenciaVista?: string;
}

export interface ConfiguracionReporteProcesoRequestDTO {
  IdProceso: number;
  TipoOrigen: 'Variables' | 'Vista';
  NombreVista?: string;
  HabilitarBusquedaFechas?: boolean;
  ColumnaFechaVista?: string;
  HabilitarHistorico?: boolean;
  ModoHistorico?: 'default' | 'sp';
  ConfiguracionHistoricoSp?: HistoricoSpConfiguracionDTO[];
  Columnas: ConfiguracionReporteColumnaRequestDTO[];
}

export interface ReporteFilaResponseDTO {
  IdEjecucion?: number;
  EsHistorico?: boolean;
  Estado?: string;
  UsuarioInicia?: string;
  FechaInicia?: string;
  UsuarioFinaliza?: string;
  FechaFinaliza?: string;
  Valores: Record<string, string | null>;
}

export interface ReporteDatosResponseDTO {
  Columnas: ConfiguracionReporteColumnaResponseDTO[];
  RecordsTotal: number;
  RecordsFiltered: number;
  Filas: ReporteFilaResponseDTO[];
}

export interface ReporteDatosRequestDTO {
  IdProceso: number;
  Skip?: number;
  PageSize?: number;
  IncluirHistoricos?: boolean;
  OrdenDescendente?: boolean;
  FechaDesde?: string;
  FechaHasta?: string;
  ColumnaFechaVista?: string;
}

export interface ProcesoConReporteDTO {
  Id: number;
  Nombre: string;
  Estado?: string;
  Version?: string;
  TieneConfiguracion: boolean;
  TipoOrigen?: 'Variables' | 'Vista';
  NombreVista?: string;
}

export interface ReporteHistoricoFilaResponseDTO {
  IdEjecucionProcesoDetalle: number;
  NombreElemento?: string;
  EstadoEjecucion?: string;
  FechaInicia?: string;
  FechaFinaliza?: string;
  Valores: Record<string, string | null>;
}

@Injectable({ providedIn: 'root' })
export class ReporteriaService {
  constructor(private http: HttpClient) {}

  getCatalogoVariables(idProceso: number): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Reporteria/GetCatalogoVariables?idProceso=${idProceso}`)
    );
  }

  getVistasDisponibles(): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Reporteria/GetVistasDisponibles`)
    );
  }

  getColumnasVista(nombreVista: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Reporteria/GetColumnasVista?nombreVista=${encodeURIComponent(nombreVista)}`)
    );
  }

  guardarConfiguracion(body: ConfiguracionReporteProcesoRequestDTO): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${AppData.API_URL}Reporteria/GuardarConfiguracion`, body)
    );
  }

  getConfiguracion(idProceso: number): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Reporteria/GetConfiguracion?idProceso=${idProceso}`)
    );
  }

  getDatosReporte(body: ReporteDatosRequestDTO): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${AppData.API_URL}Reporteria/GetDatosReporte`, body)
    );
  }

  getHistoricoEjecucion(idEjecucionProceso: number, nombreSp?: string): Promise<any> {
    const url = nombreSp
      ? `${AppData.API_URL}Reporteria/GetHistoricoEjecucion?idEjecucionProceso=${idEjecucionProceso}&nombreSp=${encodeURIComponent(nombreSp)}`
      : `${AppData.API_URL}Reporteria/GetHistoricoEjecucion?idEjecucionProceso=${idEjecucionProceso}`;
    return lastValueFrom(this.http.get<any>(url));
  }

  getHistoricoEjecucionSp(idEjecucionProceso: number, nombreSp: string): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${AppData.API_URL}Reporteria/GetHistoricoEjecucionSp`, {
        IdEjecucionProceso: idEjecucionProceso,
        NombreSp: nombreSp
      })
    );
  }

  getProcesosConConfiguracion(): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Reporteria/GetProcesosConConfiguracion`)
    );
  }

  exportarExcel(body: ReporteDatosRequestDTO): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${AppData.API_URL}Reporteria/ExportarExcel`, body, {
        responseType: 'blob' as 'json'
      })
    );
  }

  exportarExcelHistorico(body: { IdEjecucionProceso: number; NombreSp?: string }): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${AppData.API_URL}Reporteria/ExportarExcelHistorico`, body, {
        responseType: 'blob' as 'json'
      })
    );
  }

  getProcesos(): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${AppData.API_URL}Proceso/ListaProcesos`)
    );
  }
}
